/**
 * Which rows Current Medications lists: per medication name, its latest row
 * (by order date and time) decides whether it is current or inactive.
 *
 * Two exceptions:
 *   - an archived row (hold-archive, discontinue-archive) never counts as the latest.
 *     A hold archives the held row and continues the course on a new one; when the
 *     archived row sorted newer (its order stamped later), it won and the medication
 *     vanished from both lists;
 *   - "hold" (a provider's hold on all times leaves the row so) is current, like
 *     Active / Active-Pending / Pending; it was neither current nor inactive.
 * The inactive list is unchanged otherwise: a discontinued medication's latest row,
 * or every other row of a current medication (archived ones included).
 */

const CURRENT = ["active", "active-pending", "pending", "hold"];
const DISCONTINUED = ["discontinue", "discontinued"];

const statusOf = (med) => (med.status || med.medchangetype || "").toLowerCase();
const isArchived = (med) => statusOf(med).endsWith("-archive");

function newestFirst(a, b) {
  const dateA = a.orderdate || "";
  const dateB = b.orderdate || "";
  if (dateA !== dateB) return dateB.localeCompare(dateA);
  return (b.ordertime || "").localeCompare(a.ordertime || "");
}

export function latestMedsByStatus(allMeds, isDiscontinued) {
  if (!Array.isArray(allMeds)) return [];

  const medGroups = {};
  allMeds.forEach((med) => {
    const medName = (med.medname || "").trim();
    if (!medName) return;
    (medGroups[medName] = medGroups[medName] || []).push(med);
  });

  const resultMeds = [];
  Object.values(medGroups).forEach((group) => {
    group.sort(newestFirst);
    const latestMed = group.find((med) => !isArchived(med)) || group[0];
    const status = statusOf(latestMed);
    const isLatestActive = CURRENT.includes(status);

    if (isDiscontinued) {
      if (DISCONTINUED.includes(status)) {
        resultMeds.push(latestMed);
      } else if (isLatestActive) {
        resultMeds.push(...group.filter((med) => med !== latestMed));
      }
    } else if (isLatestActive) {
      resultMeds.push(latestMed);
    }
  });

  return resultMeds.sort(newestFirst);
}
